import { useTranslation } from 'next-i18next'
import { useState } from 'react'

import { COLLECTION_LIST, QUIZ_SUBJECT, CHAMPIONSHIP_IDS } from '~/types/homebaseQuiz.types'

import { QuestMetadata } from '~/types/quest'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectShowcaseBadge, setScrollToBase, setShowcaseBadge } from '~/redux/slices/homebase'

import { messagePCHomebase } from '~/homebase/components/Homebase'

import { useGetBadgesQuery } from '~/redux/apis/collection-api'

import { EventAction, logEvent } from '~/utils/analytics'

import { EventCategory } from '~/utils/analytics/events'

import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'

import LeagueBadgeItem from '~/components/QuestPanel/components/LeagueBadgeItem'

import { useGetCurrentLeagueQuery } from '~/Leaderboard/apis/leaderboard-api'

import { getLeagueName } from '~/Leaderboard/utils/utils'

import { LeagueNames } from '~/Leaderboard/types/leaderboard.types'

import useGetCompletedQuests from '../hooks/useGetCompletedQuests'

import { BadgeData } from '../type'

import QuestBox from './QuestBox'

import CollectionItem from './CollectionItem'
import CompetitionItem from './CompetitionItem'
import SuperBadgeItem from './SuperBadgeItem'

export default function Collection({
  primaryBgColor,
  primeryTxtColor,
  data,
}: {
  primaryBgColor: string
  primeryTxtColor: string
  data?: QuestMetadata[]
}): React.ReactElement {
  const { t } = useTranslation('homebase')
  const [collectionList, setCollectionList] = useState(COLLECTION_LIST.ALL_ITEMS)
  const showcaseBadge = useAppSelector(selectShowcaseBadge)
  const completedQuests = useGetCompletedQuests()
  const mathDrillQuestId = Object.values(QUIZ_SUBJECT)
  const competitionQuestId = Object.values(CHAMPIONSHIP_IDS)
  const dispatch = useAppDispatch()
  const { data: badgeData } = useGetBadgesQuery(null)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })

  const leagueRank = useGetCurrentLeagueQuery()
  const leagueName = getLeagueName(
    typeof leagueRank.data?.highestLeague === 'number' ? leagueRank.data.highestLeague : 0,
  )

  const collectionListClassNames = 'hover:cursor-pointer text-sm text-center'
  const collectionItemsSize = 'h-16 w-12 md:h-[130px] md:w-[110px] flex flex-col justify-between gap-0.5'
  const selectedCollection = 'font-bold underline'

  const onClickTrophy = ({
    subject,
    action,
    category,
    collection,
    badge,
    leagueBadge,
  }: {
    subject: QUIZ_SUBJECT | CHAMPIONSHIP_IDS | string
    action: EventAction
    category?: string
    collection: COLLECTION_LIST
    badge?: BadgeData
    leagueBadge?: LeagueNames
  }): void => {
    const eventData = {
      eventCategory: EventCategory.HOMEBASE,
      eventAction: action,
      eventLabel: subject,
    }
    logEvent(eventData)
    dispatch(setShowcaseBadge({ subject, category, collection, badgeData: badge, leagueBadge }))
    messagePCHomebase({ pause: true })
    if (isMobile) dispatch(setScrollToBase())
  }

  const onCollectionListClick = (listItem: COLLECTION_LIST): void => {
    const eventData = {
      eventCategory: EventCategory.HOMEBASE,
      eventAction: EventAction.CLICK_CATEGORY,
      eventLabel: listItem,
    }
    setCollectionList(listItem)
    logEvent(eventData)
  }

  return (
    <QuestBox
      bgColor={primaryBgColor}
      bgPadding="px-1 sm:px-3 pt-1 pb-2 sm:px-4"
      h4ClassName={`flex m-auto ${showcaseBadge && 'hidden'}`}
      isCollection
      title={t('collection')}
      txtColor={primeryTxtColor}
    >
      <div className="flex flex-col flex-wrap">
        <div className="flex flex-col items-center justify-center my-1 gap-2 leading-none pb-2 pt-1">
          <p
            className={` ${collectionListClassNames} ${
              collectionList === COLLECTION_LIST.ALL_ITEMS ? selectedCollection : ''
            }`}
            onClick={() => onCollectionListClick(COLLECTION_LIST.ALL_ITEMS)}
          >
            {t('collectionList.allRewards')}
          </p>
          <ul className="flex flex-wrap justify-center items-center gap-2">
            <li
              className={`${collectionListClassNames} ${
                collectionList === COLLECTION_LIST.BOOTCAMP ? selectedCollection : ''
              }`}
              onClick={() => onCollectionListClick(COLLECTION_LIST.BOOTCAMP)}
            >
              {t('collectionList.bootcamps')}
            </li>
            <li
              className={`${collectionListClassNames} ${
                collectionList === COLLECTION_LIST.MATH_DRILLS ? selectedCollection : ''
              }`}
              onClick={() => onCollectionListClick(COLLECTION_LIST.MATH_DRILLS)}
            >
              {t('collectionList.mathEssentials')}
            </li>
            <li
              className={`${collectionListClassNames} ${
                collectionList === COLLECTION_LIST.COMPETITION ? selectedCollection : ''
              }`}
              onClick={() => onCollectionListClick(COLLECTION_LIST.COMPETITION)}
            >
              {t('collectionList.competition')}
            </li>
          </ul>
        </div>
        <div className="flex justify-center m-auto">
          <div className="flex justify-center gap-2 flex-wrap mx-auto">
            {(collectionList === COLLECTION_LIST.ALL_ITEMS || collectionList === COLLECTION_LIST.COMPETITION) && (
              <LeagueBadgeItem
                highestLeague={leagueRank.data?.highestLeague}
                leagueName={leagueName}
                onClick={() =>
                  onClickTrophy({
                    subject: `league-${leagueName}`,
                    action: EventAction.CLICK_TROPHY,
                    collection: COLLECTION_LIST.COMPETITION,
                    leagueBadge: leagueName,
                  })
                }
              />
            )}
            {(collectionList === COLLECTION_LIST.ALL_ITEMS || collectionList === COLLECTION_LIST.BOOTCAMP) &&
              badgeData &&
              badgeData.map((superBadge: BadgeData) => (
                <SuperBadgeItem
                  CollectionItemsSize={collectionItemsSize}
                  key={superBadge.id}
                  onClickTrophy={onClickTrophy}
                  superBadge={superBadge}
                />
              ))}

            {(collectionList === COLLECTION_LIST.ALL_ITEMS || collectionList === COLLECTION_LIST.MATH_DRILLS) &&
              mathDrillQuestId.map((quiz) => (
                <CollectionItem
                  className={collectionItemsSize}
                  completed={completedQuests || []}
                  data={data}
                  id={quiz}
                  key={quiz}
                  onClick={() =>
                    onClickTrophy({
                      subject: quiz,
                      action: EventAction.CLICK_TROPHY,
                      collection: COLLECTION_LIST.MATH_DRILLS,
                    })
                  }
                />
              ))}

            {(collectionList === COLLECTION_LIST.ALL_ITEMS || collectionList === COLLECTION_LIST.COMPETITION) && (
              <>
                {competitionQuestId.map((quiz) => (
                  <CompetitionItem
                    className={collectionItemsSize}
                    completed={completedQuests || []}
                    id={quiz}
                    key={quiz}
                    onClick={() =>
                      onClickTrophy({
                        subject: quiz,
                        action: EventAction.CLICK_TROPHY,
                        collection: COLLECTION_LIST.COMPETITION,
                      })
                    }
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </QuestBox>
  )
}
